import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  ChartBarIcon,
  CurrencyPoundIcon,
  UsersIcon,
  ClockIcon,
  ArrowTrendingUpIcon as TrendingUpIcon,
} from '@heroicons/react/24/outline';
import { analyticsAPI } from '../services/api';
import LoadingSpinner from '../components/ui/LoadingSpinner';
import EVMDashboard from '../components/EVMDashboard';

// Simple bar chart component
function BarChart({ data, title, color = 'blue' }) {
  if (!data || Object.keys(data).length === 0) {
    return <p className="text-sm text-gray-500">No data available</p>;
  }

  const maxValue = Math.max(...Object.values(data));

  return (
    <div className="space-y-3">
      <h3 className="text-sm font-medium text-gray-700">{title}</h3>
      <div className="space-y-2">
        {Object.entries(data).map(([key, value]) => (
          <div key={key} className="flex items-center gap-3">
            <div className="w-24 text-sm text-gray-600 capitalize">{key.replace('_', ' ')}</div>
            <div className="flex-1 bg-gray-100 rounded-full h-6 relative">
              <div
                className={`bg-${color}-600 h-6 rounded-full flex items-center justify-end px-2 transition-all`}
                style={{ width: `${(value / maxValue) * 100}%` }}
              >
                <span className="text-xs font-medium text-white">{value}</span>
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// Metric card component
function MetricCard({ title, value, subtitle, icon: Icon, color = 'blue' }) {
  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-medium text-gray-600">{title}</h3>
        <div className={`p-2 bg-${color}-100 rounded-lg`}>
          <Icon className={`w-5 h-5 text-${color}-600`} />
        </div>
      </div>
      <div className="text-3xl font-bold text-gray-900">{value}</div>
      {subtitle && <p className="text-sm text-gray-500 mt-1">{subtitle}</p>}
    </div>
  );
}

export default function Analytics() {
  const [activeTab, setActiveTab] = useState('overview');

  // Fetch all analytics data
  const { data: budgetData, isLoading: budgetLoading } = useQuery({
    queryKey: ['analytics', 'budget'],
    queryFn: () => analyticsAPI.getBudget(),
  });

  const { data: probabilityData, isLoading: probabilityLoading } = useQuery({
    queryKey: ['analytics', 'probability'],
    queryFn: () => analyticsAPI.getSuccessProbability(),
  });

  const { data: workloadData, isLoading: workloadLoading } = useQuery({
    queryKey: ['analytics', 'workload'],
    queryFn: () => analyticsAPI.getTeamWorkload(),
  });

  const { data: dashboardData } = useQuery({
    queryKey: ['dashboard'],
    queryFn: () => analyticsAPI.getDashboard(),
  });

  const isLoading = budgetLoading || probabilityLoading || workloadLoading;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  const overview = dashboardData?.overview || {};

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Analytics</h1>
        <p className="text-gray-600">Performance metrics and insights</p>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex">
            <button
              onClick={() => setActiveTab('overview')}
              className={`py-4 px-6 font-medium text-sm border-b-2 ${
                activeTab === 'overview'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Overview
            </button>
            <button
              onClick={() => setActiveTab('evm')}
              className={`py-4 px-6 font-medium text-sm border-b-2 ${
                activeTab === 'evm'
                  ? 'border-blue-500 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Earned Value Management
            </button>
          </nav>
        </div>
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* Key Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <MetricCard
              title="Total Budget"
              value={`£${(budgetData?.total_budget || 0).toLocaleString()}`}
              subtitle={`${budgetData?.projects_with_budget || 0} projects`}
              icon={CurrencyPoundIcon}
              color="green"
            />
            <MetricCard
              title="Total Spent"
              value={`£${(budgetData?.total_spent || 0).toLocaleString()}`}
              subtitle={`${budgetData?.budget_utilization?.toFixed(1) || 0}% utilized`}
              icon={TrendingUpIcon}
              color="blue"
            />
            <MetricCard
              title="Team Members"
              value={workloadData?.total_members || 0}
              subtitle={`${workloadData?.active_members || 0} active`}
              icon={UsersIcon}
              color="purple"
            />
            <MetricCard
              title="Active Projects"
              value={overview.active_projects || 0}
              subtitle={`${overview.total_projects || 0} total`}
              icon={ChartBarIcon}
              color="orange"
            />
          </div>

          {/* Charts Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Budget by Status */}
            <div className="bg-white rounded-lg shadow p-6">
              <BarChart
                data={budgetData?.by_status || {}}
                title="Budget by Project Status"
                color="green"
              />
            </div>

            {/* Success Probability Distribution */}
            <div className="bg-white rounded-lg shadow p-6">
              <BarChart
                data={probabilityData?.distribution || {}}
                title="Success Probability Distribution"
                color="blue"
              />
            </div>

            {/* Projects by Status */}
            <div className="bg-white rounded-lg shadow p-6">
              <BarChart
                data={overview.projects_by_status || {}}
                title="Projects by Status"
                color="purple"
              />
            </div>

            {/* Projects by Priority */}
            <div className="bg-white rounded-lg shadow p-6">
              <BarChart
                data={overview.projects_by_priority || {}}
                title="Projects by Priority"
                color="orange"
              />
            </div>
          </div>

          {/* Team Workload */}
          {workloadData?.workload && Object.keys(workloadData.workload).length > 0 && (
            <div className="bg-white rounded-lg shadow p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <UsersIcon className="w-5 h-5" />
                Team Workload Distribution
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {Object.entries(workloadData.workload).map(([member, count]) => (
                  <div key={member} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                        <span className="text-blue-600 font-semibold">{member.charAt(0)}</span>
                      </div>
                      <span className="font-medium text-gray-900">{member}</span>
                    </div>
                    <span className="text-2xl font-bold text-blue-600">{count}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {activeTab === 'evm' && <EVMDashboard />}
    </div>
  );
}
